<template>
  <v-main>

    <v-container
        id="view-account-container"
        fluid
        tag="section"
        v-if="user"
    >

      <base-material-card
          icon="mdi-account-details-outline"
          class="px-5 py-3"
      >

        <div data-cy="page-messages">
          <shared-page-messages :error-messages="errorMessages"
                                :warning-messages="warningMessages"
                                :success-messages="successMessages">
          </shared-page-messages>
        </div>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-medium"
          >
            Account #:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.accountNumber }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            Name:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.names }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            Address:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.address }} <br />
            <span v-if="user.addressLine2"> {{ user.addressLine2 }} <br /> </span>
            {{ user.city }}, {{ user.state }} {{ user.postalCode }} <br />
            <span v-if="user.country"> {{ computedCountry.name }} </span>
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            Phone:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.phoneNumber }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            Email:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.email }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            Gender:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.gender }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            Birth Year:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.birthYear }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            ID Number:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.idNumber }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            ID Type:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            <span v-if="user.idType">
              {{ user.idType.name }}
            </span>
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            ID Issued By:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.idIssuedBy }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            ID Expires:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.idExpires }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            Status:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ userStatusMap[user.status] }}
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            On Suspension:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            <span v-if="user.isSuspended">Yes</span>
            <span v-else>No</span>
          </v-col>
        </v-row>

        <v-row>
          <v-col
              cols="4"
              sm="4"
              class="font-weight-bold"
          >
            Last Updated:
          </v-col>
          <v-col
              cols="8"
              sm="8"
          >
            {{ user.updated | formatDateTime }}
          </v-col>
        </v-row>

      </base-material-card>
    </v-container>

  </v-main>
</template>

<script>
import api from '@/services/api'
import geoUtils from '@/utils/geography'
import constants from "@/utils/constants";

import {mapGetters} from "vuex";
import SharedPageMessages from '@/views/components/shared/PageMessages.vue'

export default {
  name: 'CoreViewUserView',
  components: {SharedPageMessages},

  async created() {
    this.$gtag.event('view_account')
    this.userId = this.getUserId

    if (this.userId !== null) {
      await this.fetchUser()
    }
  },

  computed: {
    ...mapGetters({
      getUserId: 'getUserId'
    }),
    computedCountry: {
      get() {
        return geoUtils.findCountryByCode(this.user.country)
      },
      set(value) {
        this.user.country = value
      }
    }
  },

  watch: {
    user() {
      if (this.user.isSuspended) {
        this.warningMessages.push("Your account is currently suspended. Please visit \"View Suspensions\" page for more details")
      }
    }
  },

  methods: {
    async fetchUser() {
      return api
          .fetchUser(this.userId)
          .then(response => {
            this.user = response.data
          })
          .catch(err => {
            this.handleError(err)
          })
    },
    handleError(err) {
      let errorMessage = api.parseResponseError(err)
      this.errorMessages.push(errorMessage)
    }
  },

  data() {
    return {
      errorMessages: [],
      successMessages: [],
      warningMessages: [],
      userId: null,
      user: {
        country: 'US',
        idType: {},
        qualification: {}
      },
      dependents: [],
      userStatusMap: constants.getUserStatusMap()
    }
  }
}
</script>
